import { clearAuth, getToken, loginPath } from "./auth";
import type {
  SpeciesPestFields,
  SpeciesProfileCatalogs,
  SpeciesProfileFields,
  SpeciesProfileHistoryEntry,
  SpeciesProfileResponse,
} from "../types/species-profile";

const API_URL =
  import.meta.env.VITE_API_URL ||
  import.meta.env.NEXT_PUBLIC_API_URL ||
  "http://localhost:8000/api/v1";

type RequestOptions = RequestInit & {
  query?: Record<string, string | number | boolean | undefined | null>;
  skipAuth?: boolean;
};

function buildUrl(path: string, query?: RequestOptions["query"]) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${API_URL}${normalizedPath}`);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Explicacao do servidor para o erro.  A validacao de campos (422) devolve uma lista;
 * cada item vira uma frase legivel, sem o prefixo tecnico "Value error,".
 */
export async function errorDetail(response: Response): Promise<string> {
  const fallback = `Erro ${response.status} ${response.statusText}`.trim();
  try {
    const body = await response.json();
    const detail = body?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d: { msg?: string }) => String(d?.msg ?? "").replace(/^Value error,\s*/, ""))
        .filter(Boolean);
      if (msgs.length) return Array.from(new Set(msgs)).join(" ");
    }
  } catch {
    // corpo nao e JSON
  }
  return fallback;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { query, headers, skipAuth, ...requestOptions } = options;

  const authHeaders: Record<string, string> = { "Content-Type": "application/json" };
  if (!skipAuth) {
    const token = getToken();
    if (token) {
      authHeaders["Authorization"] = `Bearer ${token}`;
    }
  }

  const response = await fetch(buildUrl(path, query), {
    ...requestOptions,
    headers: {
      ...authHeaders,
      ...headers,
    },
  });

  if (response.status === 401) {
    clearAuth();
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      window.location.href = loginPath(window.location.pathname + window.location.search);
    }
    throw new Error("Sessao expirada. Faca login novamente.");
  }

  if (!response.ok) {
    throw new Error(await errorDetail(response));
  }

  // DELETE devolve 204 sem corpo: ler JSON aqui quebrava e a tela mostrava erro
  // mesmo com o registro ja removido.
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Multipart upload.
 *
 * Separate from apiFetch because that one forces Content-Type: application/json;
 * for FormData the browser has to set the header itself so it can include the
 * multipart boundary.
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(buildUrl(path), { method: "POST", body: form, headers });

  if (response.status === 401) {
    clearAuth();
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      window.location.href = loginPath(window.location.pathname + window.location.search);
    }
    throw new Error("Sessao expirada. Faca login novamente.");
  }
  if (!response.ok) {
    // The server explains refusals (wrong hash, file too big, not an image) in
    // `detail`; surfacing the status alone would hide the reason.
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* resposta sem corpo JSON */
    }
    throw new Error(detail);
  }
  return response.json() as Promise<T>;
}

/** Fetches a protected binary as an object URL, for <img src>. */
export async function apiFetchBlobUrl(path: string): Promise<string | null> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const response = await fetch(buildUrl(path), { headers });
  if (!response.ok) return null;
  return URL.createObjectURL(await response.blob());
}

export interface LoginResponseApi {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: number;
    uuid: string;
    name: string;
    email: string;
    tenant_id: number | null;
    role: string | null;
  };
}

// The platform this client is.  The API validates it against the user's platform
// access and stamps it into the token, so records created here are traceable to
// the website rather than to the field app.  The API defaults to FLORAIQ_WEB,
// but sending it explicitly keeps the two clients symmetric.
export const CLIENT_PLATFORM = "FLORAIQ_WEB";

export async function loginApi(matricula: string, senha: string): Promise<LoginResponseApi> {
  return apiFetch<LoginResponseApi>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ matricula, senha, platform: CLIENT_PLATFORM }),
    skipAuth: true,
  });
}

export interface UserPermissionsApi {
  role: string | null;
  role_name: string | null;
  permissions: string[];
}

export async function fetchMyPermissions(): Promise<UserPermissionsApi> {
  return apiFetch<UserPermissionsApi>("/auth/me/permissions");
}

export interface DashboardSummaryApi {
  total_projects: number;
  total_trees: number;
  total_species: number;
  total_documents: number;
  total_parties: number;
  total_rrr: number;
  total_validation_issues: number;
  total_point_cloud_assets: number;
  total_environmental_units: number;
  total_carbon_projects: number;
  status: string;
}

export interface SpeciesApi {
  id: number;
  uuid: string;
  family_id: number | null;
  family_name: string | null;
  popular_name: string;
  scientific_name: string;
  origin: string | null;
  tree_size: string | null;
  functional_group: string | null;
  wood_density: number | null;
  preliminary_carbon_factor: number | null;
  protected_species: boolean;
  invasive_species: boolean;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  registered_tree_count: number;
  /** Status da ficha botanica; null = especie ainda sem ficha. */
  profile_review_status: "RASCUNHO_IA" | "EM_REVISAO" | "REVISADO" | null;
}

export interface ProjectApi {
  project_id: number;
  project_uuid: string;
  tenant_id: number;
  client_id: number | null;
  municipality_id: number | null;
  name: string;
  project_type: string;
  status: string;
  total_trees: number;
  active_trees: number;
  high_risk_trees: number;
  species_count: number;
  inspection_count: number;
  latest_inspection_date: string | null;
  progress_percent: number;
  created_at: string;
  updated_at: string;
  client_name: string | null;
  municipality_name: string | null;
  uf: string | null;
}

export interface TreeApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number | null;
  project_name: string | null;
  spatial_unit_id: number | null;
  spatial_unit_type: string | null;
  spatial_unit_name: string | null;
  species_id: number | null;
  species_popular_name: string | null;
  species_scientific_name: string | null;
  protected_species: boolean | null;
  invasive_species: boolean | null;
  botanical_family: string | null;
  cta_code: string | null;
  public_code: string | null;
  individual_type: string | null;
  status: string;
  review_status: string | null;
  latitude: number | null;
  longitude: number | null;
  municipality_id: number | null;
  municipality_name: string | null;
  municipality_uf: string | null;
  neighborhood: string | null;
  street: string | null;
  address_reference: string | null;
  latest_inspection_id: number | null;
  latest_inspection_type: string | null;
  latest_inspection_date: string | null;
  latest_risk_assessment_id: number | null;
  latest_risk_class: string | null;
  latest_risk_score: number | null;
  latest_risk_priority: string | null;
  created_at: string;
  updated_at: string;
  /** SIRGAS 2000 / UTM 22S (EPSG:31982), derivado da geometria no servidor. */
  utm_e?: number | null;
  utm_n?: number | null;
}

export interface QuadraInventoryTreeApi {
  id: number;
  cta_code: string;
  status: string;
  street: string | null;
  created_at: string;
  species_popular_name: string | null;
  species_scientific_name: string | null;
  latitude: number;
  longitude: number;
  distance_m: number;
  inside_quadra: boolean;
  latest_validated_inspection_date: string | null;
  latest_validated_risk_class: string | null;
}

export interface QuadraTreeInventoryApi {
  quadra_id: number;
  quadra_code: string;
  quadra_name: string | null;
  inspection_code: string | null;
  inspection_date: string | null;
  ai_model: string | null;
  estimated_tree_count: number | null;
  planting_potential_count: number | null;
  risk_tree_count: number | null;
  registered_count: number;
  validated_count: number;
  high_risk_count: number;
  trees: QuadraInventoryTreeApi[];
}

/** Resposta de /territorial/tree-statistics em snake_case; convertida por mapTreeStatisticsFromApi. */
export type TreeStatisticsApi = Record<string, unknown>;

export interface TreeStatisticsQuery {
  region?: string | null;
  bairroId?: number | null;
  quadraId?: number | null;
  streetType?: string | null;
  street?: string | null;
}

export interface AreaTreeInventoryApi {
  area_piloto_id: number;
  max_distance_m: number;
  quadras_total: number;
  quadras_with_estimate: number;
  quadras_with_registered: number;
  estimated_total: number;
  registered_total: number;
  validated_total: number;
  high_risk_total: number;
  quadras: QuadraTreeInventoryApi[];
}

export interface TreesFeatureCollectionApi {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    id: number;
    geometry: { type: string; coordinates: unknown };
    properties: Record<string, unknown>;
  }>;
}

export interface InspectionApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number;
  project_name: string;
  tree_id: number;
  cta_code: string | null;
  public_code: string | null;
  species_id: number | null;
  species_popular_name: string | null;
  species_scientific_name: string | null;
  inspection_type: string;
  inspection_date: string;
  technician_party_id: number | null;
  technician_name: string | null;
  source_id: number | null;
  source_title: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number | null;
  project_name: string | null;
  source_id: number | null;
  title: string;
  document_type: string;
  document_number: string | null;
  issuing_body: string | null;
  issue_date: string | null;
  expiration_date: string | null;
  status: string;
  description: string | null;
  linked_entity_schema: string | null;
  linked_entity_table: string | null;
  linked_entity_id: number | null;
  linked_relation_type: string | null;
  linked_entity_name: string | null;
  file_count: number | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  file_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface PartyApi {
  id: number;
  uuid: string;
  tenant_id: number;
  party_type: string;
  name: string;
  legal_name: string | null;
  document_number: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  municipality_id: number | null;
  municipality_name: string | null;
  uf: string | null;
  status: string;
  total_roles: number;
  project_link_count: number;
  spatial_unit_link_count: number;
  document_count: number;
  role_types: string | null;
  created_at: string;
  updated_at: string;
}

export interface QualitySummaryApi {
  tenant_id: number;
  project_id: number;
  project_name: string;
  open_issue_count: number;
  critical_issue_count: number;
  high_issue_count: number;
  medium_issue_count: number;
  estimated_quality_score: number;
}

export interface ValidationIssueApi {
  tenant_id: number;
  project_id: number | null;
  rule_code: string;
  severity: string;
  entity_schema: string;
  entity_table: string;
  entity_id: number;
  message: string;
  detected_at: string;
}

export interface TreeMetrics3DApi {
  id: number | null;
  uuid: string | null;
  tenant_id: number;
  project_id: number | null;
  point_cloud_name: string | null;
  point_cloud_id: number | null;
  tree_id: number;
  cta_code: string | null;
  public_code: string | null;
  species_popular_name: string | null;
  species_scientific_name: string | null;
  field_inspection_id: number | null;
  field_dap_cm: number | null;
  field_height_m: number | null;
  field_crown_diameter_m: number | null;
  field_crown_area_m2: number | null;
  field_measured_at: string | null;
  height_3d_m: number | null;
  crown_diameter_3d_m: number | null;
  crown_area_3d_m2: number | null;
  crown_volume_3d_m3: number | null;
  inclination_3d_deg: number | null;
  height_delta_m: number | null;
  crown_diameter_delta_m: number | null;
  confidence_level: string | null;
  validation_status: string;
  extracted_at: string | null;
}

/** Camada externa de referencia (arvores da Prefeitura, ArcGIS). Nao e avaliacao FloraIQ. */
export interface ReferenceLayerInfoApi {
  id: number;
  codigo: string;
  nome: string | null;
  fonte_nome: string;
  orgao: string | null;
  total_validos: number;
  ultima_observacao_em: string | null;
}

export interface ReferenceClusterApi {
  lon: number;
  lat: number;
  count: number;
}

export interface ReferencePointApi {
  id: number;
  external_id: string;
  lon: number;
  lat: number;
  cdespecie: number | string | null;
  porte: string | null;
  idade: string | null;
  cap: number | string | null;
  dtlevanta: string | null;
}

export type ReferenceFeaturesApi =
  | { mode: "clusters"; items: ReferenceClusterApi[]; truncated: boolean }
  | { mode: "points"; items: ReferencePointApi[]; truncated: boolean };

export interface ReferenceRecordApi {
  id: number;
  external_id: string;
  lon: number;
  lat: number;
  attributes: Record<string, unknown>;
  primeira_observacao_em: string;
  ultima_observacao_em: string;
}

export const api = {
  health: () => apiFetch("/health"),
  dashboardSummary: () => apiFetch<DashboardSummaryApi>("/dashboard/summary"),
  dashboardOverview: () => apiFetch<DashboardOverviewApi>("/dashboard/overview"),
  bairros: () => apiFetch<BairroApi[]>("/territorial/bairros"),
  municipalBoundaryGeojson: () => apiFetch<{ type: "FeatureCollection"; features: unknown[] }>("/territorial/municipio/limite/geojson"),
  regionsGeojson: () => apiFetch<{ type: "FeatureCollection"; features: unknown[] }>("/territorial/regioes/geojson"),
  referenceLayer: (codigo: string) => apiFetch<ReferenceLayerInfoApi>(`/reference-layers/${codigo}`),
  referenceFeatures: (codigo: string, bbox: [number, number, number, number], zoom: number, signal?: AbortSignal) =>
    apiFetch<ReferenceFeaturesApi>(`/reference-layers/${codigo}/features`, {
      query: { bbox: bbox.map((v) => v.toFixed(6)).join(","), zoom },
      signal,
    }),
  referenceRecord: (codigo: string, id: number) =>
    apiFetch<ReferenceRecordApi>(`/reference-layers/${codigo}/features/${id}`),
  bairrosGeojson: () => apiFetch<{ type: "FeatureCollection"; features: unknown[] }>("/territorial/bairros/geojson"),
  treeStatistics: (q: TreeStatisticsQuery) => {
    const params = new URLSearchParams();
    if (q.region) params.set("region", q.region);
    if (q.bairroId) params.set("bairro_id", String(q.bairroId));
    if (q.quadraId) params.set("quadra_id", String(q.quadraId));
    if (q.streetType) params.set("street_type", q.streetType);
    if (q.street) params.set("street", q.street);
    const qs = params.toString();
    return apiFetch<TreeStatisticsApi>(`/territorial/tree-statistics${qs ? `?${qs}` : ""}`);
  },
  areaTreeInventory: (areaPilotoId: number) =>
    apiFetch<AreaTreeInventoryApi>(`/territorial/areas-piloto/${areaPilotoId}/tree-inventory`),
  satelliteVegetationQuadras: (areaPilotoId: number) =>
    apiFetch<QuadrasVegetationApi>(`/territorial/satellite-vegetation/quadras?area_piloto_id=${areaPilotoId}`),
  quadraSatelliteVegetation: (quadraId: number) =>
    apiFetch<VegetationSeriesApi>(`/territorial/quadras/${quadraId}/satellite-vegetation`),
  areaSatelliteVegetation: (areaPilotoId: number) =>
    apiFetch<VegetationSeriesApi>(`/territorial/areas-piloto/${areaPilotoId}/satellite-vegetation`),
  species: () => apiFetch<SpeciesApi[]>("/species"),
  speciesProfile: (speciesId: number) =>
    apiFetch<SpeciesProfileResponse>(`/species/${speciesId}/profile`),
  speciesProfileHistory: (speciesId: number) =>
    apiFetch<SpeciesProfileHistoryEntry[]>(`/species/${speciesId}/profile/history`),
  speciesProfileCatalogs: () => apiFetch<SpeciesProfileCatalogs>("/species/profile-catalogs"),
  updateSpeciesProfile: (speciesId: number, payload: Partial<SpeciesProfileFields> & { change_note?: string }) =>
    apiFetch<SpeciesProfileResponse>(`/species/${speciesId}/profile`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  reviewSpeciesProfile: (speciesId: number, note?: string) =>
    apiFetch<SpeciesProfileResponse>(`/species/${speciesId}/profile/review`, {
      method: "POST",
      body: JSON.stringify({ note: note || null }),
    }),
  addSpeciesPest: (speciesId: number, payload: SpeciesPestFields) =>
    apiFetch<SpeciesProfileResponse>(`/species/${speciesId}/pests`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateSpeciesPest: (speciesId: number, pestId: number, payload: SpeciesPestFields) =>
    apiFetch<SpeciesProfileResponse>(`/species/${speciesId}/pests/${pestId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteSpeciesPest: (speciesId: number, pestId: number) =>
    apiFetch<SpeciesProfileResponse>(`/species/${speciesId}/pests/${pestId}`, { method: "DELETE" }),
  projects: () => apiFetch<ProjectApi[]>("/projects"),
  project: (uuid: string) => apiFetch<ProjectApi>(`/projects/${uuid}`),
  tree: (treeId: number) => apiFetch<TreeApi>(`/trees/${treeId}`),
  treeByCode: (code: string) => apiFetch<TreeApi>(`/trees/by-code/${encodeURIComponent(code)}`),
  trees: (projectId?: number) => apiFetch<TreeApi[]>("/trees", { query: { project_id: projectId } }),
  treesGeoJson: (projectId?: number) =>
    apiFetch<TreesFeatureCollectionApi>("/trees/geojson", { query: { project_id: projectId } }),
  inspections: (projectId?: number) =>
    apiFetch<InspectionApi[]>("/inspections", { query: { project_id: projectId } }),
  documents: (projectId?: number) => apiFetch<DocumentApi[]>("/documents", { query: { project_id: projectId } }),
  parties: () => apiFetch<PartyApi[]>("/parties"),
  qualitySummary: () => apiFetch<QualitySummaryApi[]>("/quality/summary"),
  qualityIssues: (projectId?: number, severity?: string) =>
    apiFetch<ValidationIssueApi[]>("/quality/issues", { query: { project_id: projectId, severity } }),
  pointCloudTreeMetrics: (projectId?: number) =>
    apiFetch<TreeMetrics3DApi[]>("/point-cloud/tree-metrics", { query: { project_id: projectId } }),
  areasPiloto: () => apiFetch<AreaPilotoApi[]>("/areas-piloto"),
  quadras: (areaPilotoId?: number) => apiFetch<QuadraApi[]>("/quadras", { query: { area_piloto_id: areaPilotoId } }),
  inspectionsWithQuadras: () => apiFetch<InspectionQuadraLinkApi[]>("/territorial/inspections-with-quadras"),
  territorialInspections: () => apiFetch<TerritorialInspectionSummaryApi[]>("/territorial-inspections"),
  territorialInspectionDetail: (inspectionCode: string) =>
    apiFetch<TerritorialInspectionDetailApi>(`/territorial-inspections/${encodeURIComponent(inspectionCode)}`),
  territorialInspectionObjects: (inspectionCode: string) =>
    apiFetch<DetectedObjectApi[]>(`/territorial-inspections/${encodeURIComponent(inspectionCode)}/objects`),
  territorialInspectionFieldValidationNotes: (inspectionCode: string) =>
    apiFetch<FieldValidationNoteApi[]>(
      `/territorial-inspections/${encodeURIComponent(inspectionCode)}/field-validation-notes`
    ),
  territorialIndicatorsSummary: () => apiFetch<TerritorialIndicatorsSummaryApi>("/territorial-indicators/summary"),
  municipalities: () => apiFetch<MunicipalityApi[]>("/municipalities"),
  clients: () => apiFetch<ClientApi[]>("/clients"),
  createProject: (data: ProjectCreatePayload) =>
    apiFetch<ProjectCreatedApi>("/projects", { method: "POST", body: JSON.stringify(data) }),
  createMunicipality: (data: MunicipalityCreatePayload) =>
    apiFetch<MunicipalityApi>("/municipalities", { method: "POST", body: JSON.stringify(data) }),
  createAreaPiloto: (data: AreaPilotoCreatePayload) =>
    apiFetch<AreaPilotoCreatedApi>("/areas-piloto", { method: "POST", body: JSON.stringify(data) }),
};

export function territorialInspectionImageUrl(inspectionCode: string): string {
  return buildUrl(`/territorial-inspections/${encodeURIComponent(inspectionCode)}/image`);
}

export async function uploadTerritorialImage(file: File): Promise<{ filename: string; size_bytes: number }> {
  const form = new FormData();
  form.append("file", file);
  const url = buildUrl("/territorial-inspections/upload-image");
  const res = await fetch(url, { method: "POST", body: form });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Upload falhou: ${res.status}`);
  }
  return res.json();
}

export async function importTerritorialInspectionJson(
  payload: Record<string, unknown>,
  opts?: { projectId?: number; areaPilotoId?: number },
): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>("/territorial-inspections/import-json", {
    method: "POST",
    body: JSON.stringify(payload),
    query: {
      project_id: opts?.projectId,
      area_piloto_id: opts?.areaPilotoId,
    },
  });
}

// --- Wizard de cadastramento de projetos ---

export interface MunicipalityApi {
  id: number;
  uuid: string;
  ibge_code: string | null;
  name: string;
  uf: string;
  status: string;
}

export interface MunicipalityCreatePayload {
  name: string;
  uf: string;
  ibge_code?: string | null;
}

export interface ClientApi {
  id: number;
  uuid: string;
  name: string;
  legal_name: string | null;
  client_type: string | null;
  status: string;
}

export interface ProjectCreatePayload {
  name: string;
  project_type: string;
  description?: string | null;
  client_id?: number | null;
  municipality_id?: number | null;
  start_date?: string | null;
  expected_end_date?: string | null;
}

export interface ProjectCreatedApi {
  id: number;
  uuid: string;
  name: string;
  project_type: string;
  status: string;
}

export interface AreaPilotoCreatePayload {
  name: string;
  code: string;
  project_id?: number | null;
  municipality_id?: number | null;
  neighborhood?: string | null;
  expected_block_count?: number | null;
  description?: string | null;
}

export interface AreaPilotoCreatedApi {
  id: number;
  uuid: string;
  name: string;
  code: string;
}

// --- Território > Área piloto / Quadras (camada operacional real) --------

export interface GeoJSONGeometryApi {
  type: string;
  coordinates?: unknown;
  geometries?: GeoJSONGeometryApi[];
}

export interface AreaPilotoApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number | null;
  municipality_id: number | null;
  municipality_name: string | null;
  uf: string | null;
  name: string;
  code: string | null;
  neighborhood: string | null;
  expected_block_count: number | null;
  total_quadras: number;
  total_trees: number;
  total_postes: number;
  total_redes_aereas: number;
  total_conflitos_alta_critica: number;
  area_ha: number | null;
  geom: GeoJSONGeometryApi | null;
  status: string;
  updated_at: string;
}

export interface QuadraApi {
  id: number;
  uuid: string;
  tenant_id: number;
  area_piloto_id: number;
  area_piloto_name: string | null;
  spatial_unit_id: number | null;
  code: string;
  name: string | null;
  neighborhood: string | null;
  area_m2: number | null;
  perimeter_m: number | null;
  total_trees: number;
  total_individuos_arboreos: number;
  total_postes: number;
  total_redes_aereas: number;
  total_interferencias: number;
  total_interferencias_criticas: number;
  area_permeavel_m2: number;
  area_impermeavel_m2: number;
  geom: GeoJSONGeometryApi | null;
  status: string;
  updated_at: string;
}

export interface InspectionQuadraLinkApi {
  inspection_id: number;
  inspection_uuid: string;
  inspection_code: string;
  tenant_id: number;
  project_id: number | null;
  inspection_area_piloto_id: number | null;
  quadra_id: number | null;
  quadra_uuid: string | null;
  quadra_area_piloto_id: number | null;
  quadra_code: string | null;
  quadra_name: string | null;
  inspection_block_code: string | null;
  inspection_neighborhood: string | null;
  quadra_neighborhood: string | null;
  inspection_status: string;
  processing_status: string;
  field_validation_status: string;
  ai_model: string | null;
  analysis_version: string | null;
  inspection_date: string;
  image_filename: string | null;
  confidence_score: number | null;
  link_method: string;
  link_confidence: string;
  total_code_matches: number;
  tree_canopy_percent: number | null;
  impervious_area_percent: number | null;
  heat_island_risk: string | null;
  tree_deficit_level: string | null;
  estimated_tree_count: number | null;
  planting_potential_count: number | null;
  inventory_canopy_coverage_percent: number | null;
  quadra_area_m2: number | null;
  quadra_perimeter_m: number | null;
  geom: GeoJSONGeometryApi | null;
  created_at: string;
  updated_at: string;
}

// --- Território > Inspeções ---------------------------------------------

export interface TerritorialInspectionSummaryApi {
  tenant_id: number;
  neighborhood: string | null;
  block_code: string | null;
  inspection_id: number;
  uuid: string;
  inspection_code: string;
  previous_inspection_id: number | null;
  previous_inspection_code: string | null;
  inspection_date: string;
  source_type: string;
  source_reference: string | null;
  image_uri: string | null;
  image_filename: string | null;
  responsible_party_id: number | null;
  responsible_party_name: string | null;
  processing_status: string;
  data_classification: string;
  intended_use: string;
  field_validation_required: boolean;
  field_validation_status: string;
  status: string;
  confidence_score: number | null;
  estimated_tree_count: number | null;
  canopy_coverage_percent: number | null;
  planting_potential_count: number | null;
  tree_deficit_count: number | null;
  risk_tree_count: number | null;
  field_validation_notes_count: number;
  created_at: string;
  updated_at: string;
}

export interface TerritorialRoadApi {
  road_name: string | null;
  road_type: string | null;
  pavement_type: string | null;
  pavement_condition: string | null;
  sidewalk_condition: string | null;
  drainage_condition: string | null;
  width_m: number | null;
  planting_potential_count: number | null;
  tree_conflict_count: number | null;
  notes: string | null;
}

export interface TerritorialLandUseApi {
  land_use_type: string;
  area_percent: number | null;
  area_m2: number | null;
  intensity_level: string | null;
  notes: string | null;
}

export interface TerritorialCoverageApi {
  coverage_type: string;
  area_percent: number | null;
  area_m2: number | null;
  permeability_class: string | null;
  heat_island_weight: number | null;
  confidence_score: number | null;
  notes: string | null;
}

export interface TerritorialTreeInventorySummaryApi {
  estimated_tree_count: number | null;
  adult_tree_count: number | null;
  young_tree_count: number | null;
  palm_count: number | null;
  shrub_count: number | null;
  canopy_coverage_percent: number | null;
  estimated_canopy_area_m2: number | null;
  planting_potential_count: number | null;
  tree_deficit_count: number | null;
  risk_tree_count: number | null;
  species_notes: string | null;
  notes: string | null;
}

export interface TerritorialIndicatorApi {
  indicator_code: string;
  indicator_name: string | null;
  category: string | null;
  value_numeric: number | null;
  value_text: string | null;
  unit: string | null;
  risk_level: string | null;
  confidence_score: number | null;
  notes: string | null;
}

export interface TerritorialRecommendationApi {
  recommendation_type: string;
  priority: string;
  description: string;
  target_location: string | null;
  estimated_quantity: number | null;
  expected_impact: string | null;
  status: string;
}

export interface TerritorialInspectionDetailApi extends TerritorialInspectionSummaryApi {
  report_title: string | null;
  report_text: string | null;
  roads: TerritorialRoadApi[];
  land_use: TerritorialLandUseApi[];
  coverage: TerritorialCoverageApi[];
  tree_inventory_summary: TerritorialTreeInventorySummaryApi | null;
  indicators: TerritorialIndicatorApi[];
  recommendations: TerritorialRecommendationApi[];
}

export interface DetectedObjectApi {
  id: number;
  uuid: string;
  tenant_id: number;
  inspection_code: string;
  block_code: string | null;
  neighborhood: string | null;
  inspection_id: number;
  object_code: string | null;
  object_type: string;
  object_label: string | null;
  confidence_score: number | null;
  detection_method: string | null;
  source_object_id: string | null;
  height_estimated_m: number | null;
  crown_diameter_estimated_m: number | null;
  canopy_area_m2: number | null;
  condition_status: string | null;
  risk_level: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface FieldValidationNoteApi {
  id: number;
  uuid: string;
  tenant_id: number;
  inspection_code: string;
  block_code: string | null;
  neighborhood: string | null;
  inspection_id: number;
  note_type: string;
  description: string;
  priority: string;
  status: string;
  responsible_party_id: number | null;
  responsible_party_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface TerritorialIndicatorsSummaryApi {
  avg_tree_coverage_by_neighborhood: Array<{
    tenant_id: number;
    neighborhood: string | null;
    total_inspections: number;
    avg_canopy_coverage_percent: number | null;
    avg_estimated_tree_count: number | null;
  }>;
  heat_island_ranking: Array<{
    tenant_id: number;
    neighborhood: string | null;
    block_code: string | null;
    inspection_id: number;
    heat_island_risk_score: number | null;
    risk_level: string | null;
    updated_at: string;
  }>;
  estimated_trees_by_block: Array<{
    tenant_id: number;
    neighborhood: string | null;
    block_code: string | null;
    inspection_id: number;
    estimated_tree_count: number;
    risk_tree_count: number;
    planting_potential_count: number;
  }>;
  planting_potential_by_road: Array<{
    tenant_id: number;
    road_name: string | null;
    road_segments: number;
    total_planting_potential: number;
  }>;
  avg_impermeabilization: Array<{
    tenant_id: number;
    neighborhood: string | null;
    avg_impermeabilization_percent: number | null;
  }>;
  tree_deficit_ranking: Array<{
    tenant_id: number;
    neighborhood: string | null;
    block_code: string | null;
    inspection_id: number;
    tree_deficit_count: number;
    planting_potential_count: number;
    canopy_coverage_percent: number;
  }>;
}

// --- Editor Territorial WebGIS -------------------------------------------

export interface RascunhoFeicaoApi {
  id: string;
  projeto_id: string;
  quadra_id: string | null;
  projeto_ref_id: number | null;
  quadra_ref_id: number | null;
  area_piloto_ref_id: number | null;
  camada_destino: string;
  tipo_objeto: string;
  tipo_geometria: string;
  geom: GeoJSONGeometryApi | null;
  propriedades: Record<string, unknown>;
  status: string;
  fonte: string;
  validacao: string;
  area_m2: number | null;
  comprimento_m: number | null;
  perimetro_m: number | null;
  publicado_schema: string | null;
  publicado_tabela: string | null;
  publicado_id: string | null;
  criado_por: string | null;
  revisado_por: string | null;
  aprovado_por: string | null;
  publicado_por: string | null;
  criado_em: string;
  atualizado_em: string;
  revisado_em: string | null;
  aprovado_em: string | null;
  publicado_em: string | null;
}

export interface RascunhoFeicaoCreatePayload {
  projeto_id: string;
  quadra_id?: string | null;
  projeto_ref_id?: number | null;
  quadra_ref_id?: number | null;
  area_piloto_ref_id?: number | null;
  camada_destino: string;
  tipo_objeto: string;
  geom: GeoJSONGeometryApi;
  propriedades?: Record<string, unknown>;
  criado_por?: string | null;
}

export interface RascunhoFeicaoUpdatePayload {
  geom?: GeoJSONGeometryApi;
  propriedades?: Record<string, unknown>;
}

export interface RascunhoStatusActionPayload {
  usuario_id?: string | null;
  motivo?: string | null;
}

export interface RascunhoPublishResultApi {
  rascunho_id: string;
  camada_destino: string;
  publicado_id: string;
  objeto_territorial_id: string;
  status: string;
}

export interface CamadaGeoApi {
  id: string;
  nome: string;
  schema_name: string;
  table_name: string;
  camada_destino: string;
  tipo_objeto: string;
  tipo_geometria: string;
  descricao: string | null;
  status: string;
}

export interface CamadaFeatureApi {
  id: string;
  rascunho_id: string | null;
  quadra_id: string | null;
  geom: GeoJSONGeometryApi | null;
  propriedades: Record<string, unknown>;
  fonte: string;
  validacao: string;
  publicado_em: string;
}

export const editorApi = {
  createRascunho: (payload: RascunhoFeicaoCreatePayload) =>
    apiFetch<RascunhoFeicaoApi>("/editor/rascunhos", { method: "POST", body: JSON.stringify(payload) }),
  rascunhos: (filters?: { status?: string; quadra_ref_id?: number; projeto_ref_id?: number; camada_destino?: string }) =>
    apiFetch<RascunhoFeicaoApi[]>("/editor/rascunhos", { query: filters }),
  rascunho: (id: string) => apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}`),
  updateRascunho: (id: string, payload: RascunhoFeicaoUpdatePayload) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteRascunho: (id: string) =>
    apiFetch<void>(`/editor/rascunhos/${encodeURIComponent(id)}`, { method: "DELETE" }),
  enviarRevisao: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}/enviar-revisao`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  aprovar: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}/aprovar`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  rejeitar: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}/rejeitar`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  solicitarAjuste: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}/solicitar-ajuste`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  publicar: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoPublishResultApi>(`/editor/rascunhos/${encodeURIComponent(id)}/publicar`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  exportGeoJson: (filters?: { status?: string; quadra_ref_id?: number }) =>
    apiFetch<{ type: string; features: unknown[] }>("/editor/export/geojson", { query: filters }),
  camadas: () => apiFetch<CamadaGeoApi[]>("/editor/camadas"),
  camadaFeatures: (camadaId: string) =>
    apiFetch<CamadaFeatureApi[]>(`/editor/camadas/${encodeURIComponent(camadaId)}/features`),
};

// --- Arboricultural Inspections (Inventário Arbóreo / Vistoria Técnica) ------

export interface ArborInspectionDetailApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number;
  tree_id: number;
  inspection_type: string;
  inspection_date: string;
  workflow_status: string;
  status: string;
  inspector_name: string | null;
  professional_council: string | null;
  professional_registration: string | null;
  location_confirmed: boolean;
  botanical_identification_status: string | null;
  botanical_notes: string | null;
  diagnosis: string | null;
  technical_opinion: string | null;
  requires_monitoring: boolean;
  next_inspection_suggested_at: string | null;
  notes: string | null;
  version: number;
  submitted_at: string | null;
  submitted_by: number | null;
  submitted_by_name?: string | null;
  validated_at: string | null;
  validated_by: number | null;
  validated_by_name?: string | null;
  returned_at: string | null;
  returned_by: number | null;
  returned_by_name?: string | null;
  return_reason: string | null;
  cancelled_at: string | null;
  cancelled_by: number | null;
  cancelled_by_name?: string | null;
  cancel_reason?: string | null;
  created_at: string;
  updated_at: string;

  measurement: ArborMeasurementApi | null;
  stems: ArborStemMeasurementApi[];
  health_assessment: ArborHealthAssessmentApi | null;
  health_findings: ArborHealthFindingApi[];
  mechanical_findings: ArborMechanicalFindingApi[];
  risk_assessment: ArborRiskAssessmentApi | null;
  risk_targets: ArborRiskTargetApi[];
  interferences: ArborInterferenceApi[];
  recommendations: ArborRecommendationApi[];
  evidence: ArborEvidenceApi[];
  compensation: ArborCompensationApi | null;
}

export interface ArborInspectionSummaryApi {
  id: number;
  uuid: string;
  tree_id: number;
  inspection_type: string;
  inspection_date: string;
  workflow_status: string;
  inspector_name: string | null;
  notes: string | null;
  created_at: string;
  dap_cm: number | null;
  height_m: number | null;
  health_status: string | null;
  risk_class: string | null;
  risk_priority: string | null;
  evidence_count: number;
  recommendation_count: number;
}

export interface ArborMeasurementApi {
  id: number;
  dap_cm: number | null;
  cap_cm: number | null;
  height_m: number | null;
  crown_diameter_m: number | null;
  inclination_deg: number | null;
  measurement_source: string | null;
  created_at: string;
}

export interface ArborStemMeasurementApi {
  id: number;
  uuid: string;
  sequence: number;
  cap_cm: number | null;
  dap_cm: number | null;
  measurement_method: string | null;
  height_m: number | null;
  notes: string | null;
  created_at: string;
}

export interface ArborHealthAssessmentApi {
  id: number;
  general_status: string | null;
  phytosanitary_class?: string | null;
  root_condition?: string | null;
  root_notes?: string | null;
  pests: boolean | null;
  diseases: boolean | null;
  fungi: boolean | null;
  termites: boolean | null;
  cavities: boolean | null;
  rot: boolean | null;
  dry_branches: boolean | null;
  exposed_roots: boolean | null;
  abnormal_inclination: boolean | null;
  inclination_angle_deg: number | null;
  inclination_direction: string | null;
  exposed_roots_detail: string | null;
  undermined_roots: boolean | null;
  damaged_roots: boolean | null;
  construction_damage: boolean | null;
  soil_compaction: boolean | null;
  root_plate_movement: boolean | null;
  notes: string | null;
  created_at: string;
}

export interface ArborHealthFindingApi {
  id: number;
  uuid: string;
  finding_type: string;
  location: string | null;
  severity: string | null;
  notes: string | null;
  created_at: string;
}

export interface ArborMechanicalFindingApi {
  id: number;
  uuid: string;
  finding_type: string;
  location: string | null;
  severity: string | null;
  notes: string | null;
  created_at: string;
}

export interface ArborRiskAssessmentApi {
  id: number;
  risk_class: string | null;
  risk_score: number | null;
  priority: string | null;
  pedestrians: boolean | null;
  vehicles: boolean | null;
  buildings: boolean | null;
  power_grid: boolean | null;
  risk_criteria: Record<string, unknown> | null;
  target_class?: string | null;
  priority_class?: string | null;
  priority_due_at?: string | null;
  priority_deadline_label?: string | null;
  priority_suggested?: string | null;
  priority_suggestion_reasons?: string[];
  notes: string | null;
  created_at: string;
}

export interface ArborRiskTargetApi {
  id: number;
  uuid: string;
  target_type: string;
  occupancy: string | null;
  notes: string | null;
  created_at: string;
}

export interface ArborInterferenceApi {
  id: number;
  interference_type: string | null;
  intensity: string | null;
  description: string | null;
  created_at: string;
}

export interface ArborRecommendationApi {
  id: number;
  recommendation_type: string | null;
  priority: string | null;
  justification: string | null;
  status: string | null;
  created_at: string;
}

export interface ArborEvidenceApi {
  id: number;
  uuid: string;
  evidence_type: string | null;
  file_path: string;
  thumbnail_path: string | null;
  original_filename: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  description: string | null;
  latitude: number | null;
  longitude: number | null;
  captured_at: string | null;
  uploaded_at: string;
  created_at: string;
  invalidated_at?: string | null;
  invalidated_by?: number | null;
  invalidation_reason?: string | null;
}

export interface ArborCompensationApi {
  id: number;
  uuid: string;
  compensation_indicated: boolean;
  proposed_seedling_quantity: number | null;
  proposed_species: unknown[] | Record<string, unknown> | null;
  notes: string | null;
  created_at: string;
}

export interface ArborInspectionCreatePayload {
  inspection_type: string;
  inspection_date: string;
  notes?: string | null;
}

export interface ArborInspectionUpdatePayload {
  inspection_date?: string | null;
  notes?: string | null;
  inspector_name?: string | null;
  professional_council?: string | null;
  professional_registration?: string | null;
  location_confirmed?: boolean | null;
  botanical_identification_status?: string | null;
  botanical_notes?: string | null;
  diagnosis?: string | null;
  technical_opinion?: string | null;
  requires_monitoring?: boolean | null;
  next_inspection_suggested_at?: string | null;
  measurement?: {
    dap_cm?: number | null;
    cap_cm?: number | null;
    height_m?: number | null;
    crown_diameter_m?: number | null;
    inclination_deg?: number | null;
    measurement_source?: string | null;
  } | null;
  stems?: Array<{
    sequence: number;
    cap_cm?: number | null;
    dap_cm?: number | null;
    measurement_method?: string | null;
    height_m?: number | null;
    notes?: string | null;
  }> | null;
  health_assessment?: {
    general_status?: string | null;
    pests?: boolean;
    diseases?: boolean;
    fungi?: boolean;
    termites?: boolean;
    cavities?: boolean;
    rot?: boolean;
    dry_branches?: boolean;
    exposed_roots?: boolean;
    abnormal_inclination?: boolean;
    inclination_angle_deg?: number | null;
    inclination_direction?: string | null;
    exposed_roots_detail?: string | null;
    undermined_roots?: boolean;
    damaged_roots?: boolean;
    construction_damage?: boolean;
    soil_compaction?: boolean;
    root_plate_movement?: boolean;
    notes?: string | null;
  } | null;
  health_findings?: Array<{
    finding_type: string;
    location?: string | null;
    severity?: string | null;
    notes?: string | null;
  }> | null;
  mechanical_findings?: Array<{
    finding_type: string;
    location?: string | null;
    severity?: string | null;
    notes?: string | null;
  }> | null;
  risk_assessment?: {
    risk_class?: string | null;
    risk_score?: number | null;
    priority?: string | null;
    pedestrians?: boolean;
    vehicles?: boolean;
    buildings?: boolean;
    power_grid?: boolean;
    risk_criteria?: Record<string, unknown> | null;
    notes?: string | null;
  } | null;
  risk_targets?: Array<{
    target_type: string;
    occupancy?: string | null;
    notes?: string | null;
  }> | null;
  interferences?: Array<{
    interference_type?: string | null;
    intensity?: string | null;
    description?: string | null;
  }> | null;
  recommendations?: Array<{
    recommendation_type?: string | null;
    priority?: string | null;
    justification?: string | null;
  }> | null;
  compensation?: {
    compensation_indicated?: boolean;
    proposed_seedling_quantity?: number | null;
    proposed_species?: unknown[] | Record<string, unknown> | null;
    notes?: string | null;
  } | null;
}

export interface ArborWorkflowActionPayload {
  reason?: string | null;
}

export const inspectionApi = {
  listByTree: (treeId: number) =>
    apiFetch<ArborInspectionSummaryApi[]>(`/trees/${treeId}/inspections`),
  create: (treeId: number, payload: ArborInspectionCreatePayload) =>
    apiFetch<ArborInspectionDetailApi>(`/trees/${treeId}/inspections`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  detail: (inspectionId: number) =>
    apiFetch<ArborInspectionDetailApi>(`/inspections/${inspectionId}/detail`),
  update: (inspectionId: number, payload: ArborInspectionUpdatePayload) =>
    apiFetch<ArborInspectionDetailApi>(`/inspections/${inspectionId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  submit: (inspectionId: number) =>
    apiFetch<ArborInspectionDetailApi>(`/inspections/${inspectionId}/submit`, { method: "POST" }),
  startReview: (inspectionId: number) =>
    apiFetch<ArborInspectionDetailApi>(`/inspections/${inspectionId}/start-review`, { method: "POST" }),
  returnInspection: (inspectionId: number, payload?: ArborWorkflowActionPayload) =>
    apiFetch<ArborInspectionDetailApi>(`/inspections/${inspectionId}/return`, {
      method: "POST",
      body: JSON.stringify(payload ?? {}),
    }),
  validate: (inspectionId: number) =>
    apiFetch<ArborInspectionDetailApi>(`/inspections/${inspectionId}/validate`, { method: "POST" }),
  conclude: (inspectionId: number) =>
    apiFetch<ArborInspectionDetailApi>(`/inspections/${inspectionId}/conclude`, { method: "POST" }),
  cancel: (inspectionId: number, payload?: ArborWorkflowActionPayload) =>
    apiFetch<ArborInspectionDetailApi>(`/inspections/${inspectionId}/cancel`, {
      method: "POST",
      body: JSON.stringify(payload ?? {}),
    }),
  // Antes o fetch ia sem o token e todo envio pelo site falhava com 401.
  // apiUpload envia o token, trata sessao expirada e repassa o `detail` do servidor.
  uploadEvidence: async (inspectionId: number, file: File, opts?: {
    evidenceType?: string;
    description?: string;
    latitude?: number;
    longitude?: number;
    clientChecksumSha256?: string | null;
  }) => {
    const form = new FormData();
    form.append("file", file);
    if (opts?.evidenceType) form.append("evidence_type", opts.evidenceType);
    if (opts?.description) form.append("description", opts.description);
    if (opts?.latitude != null) form.append("latitude", String(opts.latitude));
    if (opts?.longitude != null) form.append("longitude", String(opts.longitude));
    if (opts?.clientChecksumSha256) form.append("client_checksum_sha256", opts.clientChecksumSha256);
    return apiUpload<ArborEvidenceApi>(`/inspections/${inspectionId}/evidence`, form);
  },
  /** Arquivo da evidencia (foto, video ou audio) como object URL autenticada. */
  evidenceFileUrl: (inspectionId: number, evidenceId: number) =>
    apiFetchBlobUrl(`/inspections/${inspectionId}/evidence/${evidenceId}/file`),
  deleteEvidence: (inspectionId: number, evidenceId: number) =>
    apiFetch<void>(`/inspections/${inspectionId}/evidence/${evidenceId}`, { method: "DELETE" }),
  /** Troca a categoria da foto (exemplar ou local).  Apos o envio, so quem homologa, com motivo. */
  reclassifyEvidence: (inspectionId: number, evidenceId: number, evidenceType: string, reason?: string) =>
    apiFetch<{ id: number; evidence_type: string; previous_type: string | null; classified_at: string }>(
      `/inspections/${inspectionId}/evidence/${evidenceId}/classification`,
      { method: "PATCH", body: JSON.stringify({ evidence_type: evidenceType, reason: reason || null }) },
    ),
  evidenceClassificationHistory: (inspectionId: number, evidenceId: number) =>
    apiFetch<EvidenceClassificationEntryApi[]>(
      `/inspections/${inspectionId}/evidence/${evidenceId}/classification-history`,
    ),
  /** Depois do envio a evidencia nao se exclui: invalida-se, com motivo. */
  invalidateEvidence: (inspectionId: number, evidenceId: number, reason: string) =>
    apiFetch<{ id: number; invalidated_at: string }>(
      `/inspections/${inspectionId}/evidence/${evidenceId}/invalidate`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
  /** Linha do tempo do exemplar (eventos reais, com autoria). */
  treeTimeline: (treeId: number) => apiFetch<TreeEventApi[]>(`/trees/${treeId}/timeline`),
  /** Evidencias de todas as vistorias do exemplar, cada uma com o fato de origem. */
  treeEvidence: (treeId: number) => apiFetch<TreeEvidenceApi[]>(`/trees/${treeId}/evidence`),
  /** O que o manual ainda exige para enviar e para validar (mesma regra do servidor). */
  readiness: (inspectionId: number) =>
    apiFetch<InspectionReadinessApi>(`/inspections/${inspectionId}/readiness`),
};

export interface ReadinessIssueApi {
  code: string;
  message: string;
  blocking: boolean;
  step: string | null;
}

export interface InspectionReadinessApi {
  inspection_id: number;
  workflow_status: string;
  can_submit: boolean;
  can_validate: boolean;
  submission: ReadinessIssueApi[];
  validation: ReadinessIssueApi[];
}

export interface CtaCatalogItemApi {
  code: string;
  name: string;
  caution: string;
  stored_statuses?: string[];
  stored_labels?: Record<string, string>;
}

export interface CtaCatalogsApi {
  homologation: {
    version: string;
    status: string;
    decided_on: string;
    decided_by: string;
    formal_record: string;
    scale_origin: string;
    variables_origin: string;
    not_included: string;
  };
  phytosanitary: CtaCatalogItemApi[];
  root_classes: CtaCatalogItemApi[];
  target_classes: CtaCatalogItemApi[];
  priority_homologation?: { version: string; status: string; formal_record: string; deadline_origin: string; note: string };
  priority_classes?: CtaPriorityClassApi[];
  field_limits?: {
    version: string;
    status: string;
    cap_dap_tolerance: number;
    fields: Record<string, CtaFieldLimitApi>;
  };
}

export interface CtaFieldLimitApi {
  label: string;
  unit: string;
  min: number;
  max: number;
  min_exclusive?: boolean;
  plausible_min?: number;
  plausible_max?: number;
  step: number;
}

export interface CtaPriorityClassApi {
  code: string;
  name: string;
  deadline_hours: number;
  deadline_label: string;
  legacy_priority: string;
  triggers: string[];
  first_action: string;
  responsible: string;
  urgency_handling: string;
  caution: string;
}

export const ctaCatalogsApi = {
  get: () => apiFetch<CtaCatalogsApi>("/cta/catalogs"),
};

// --- Campaigns (Campanhas de Campo) -----------------------------------------

export interface CampaignSummaryApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number;
  project_name: string;
  area_piloto_id: number | null;
  area_piloto_name: string | null;
  code: string;
  name: string;
  campaign_type: string;
  status: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  target_tree_count: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  member_count: number;
  quadra_count: number;
  quadras_concluidas: number;
  registered_tree_count: number;
  progress_percent: number;
}

export interface CampaignCreatePayload {
  project_id: number;
  area_piloto_id?: number | null;
  code: string;
  name: string;
  campaign_type?: string;
  description?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  target_tree_count?: number | null;
  notes?: string | null;
}

export interface CampaignUpdatePayload {
  name?: string | null;
  description?: string | null;
  status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  target_tree_count?: number | null;
  notes?: string | null;
}

export interface CampaignMemberApi {
  id: number;
  campaign_id: number;
  user_id: number | null;
  party_id: number | null;
  member_role: string;
  member_name: string | null;
  professional_council: string | null;
  professional_registration: string | null;
  is_active: boolean;
  joined_at: string;
}

export interface CampaignMemberCreatePayload {
  user_id?: number | null;
  party_id?: number | null;
  member_role?: string;
  member_name?: string | null;
  professional_council?: string | null;
  professional_registration?: string | null;
}

export interface CampaignQuadraProgressApi {
  id: number;
  campaign_id: number;
  campaign_code: string;
  campaign_name: string;
  quadra_id: number;
  quadra_code: string;
  quadra_name: string | null;
  status: string;
  target_tree_count: number | null;
  registered_tree_count: number;
  started_at: string | null;
  completed_at: string | null;
  notes: string | null;
  progress_percent: number;
}

export interface CampaignQuadraAddPayload {
  quadra_id: number;
  target_tree_count?: number | null;
  notes?: string | null;
}

export interface TreeCreatePayload {
  project_id: number;
  /** Vizinhos que o operador confirmou serem outros exemplares (nunca fusão automática). */
  acknowledged_neighbor_ids?: number[];
  species_id?: number | null;
  cta_code?: string;
  species_name?: string;
  public_code?: string | null;
  individual_type?: string;
  latitude: number;
  longitude: number;
  municipality_id?: number | null;
  neighborhood?: string | null;
  street?: string | null;
  address_reference?: string | null;
  registration_source?: string;
  campaign_id?: number | null;
  registration_confidence?: string | null;
  field_notes?: string | null;
  // Location provenance (migration 054): how the coordinate was obtained.
  // registration_platform is NOT here on purpose — the API derives it from the
  // authenticated session so a payload cannot claim to be the field app.
  positioning_method?: string | null;
  location_accuracy_m?: number | null;
  location_source?: string | null;
  field_confirmed?: boolean;
}

/** What the geometry answers about a coordinate, so nobody retypes it. */
export interface PointTerritorialContextApi {
  neighborhood: string | null;
  neighborhood_id?: string | null;
  region?: string | null;
  municipality_id: number | null;
  municipality_name: string | null;
  quadra_id: number | null;
  quadra_code: string | null;
  area_piloto_id: number | null;
  area_piloto_code: string | null;
  cadastral_block_code: string | null;
  lot_number: string | null;
  lot_registration: string | null;
  street: string | null;
  street_distance_m: number | null;
  lot_relation_suggestion?: LotRelationSuggestionApi | null;
  utm_e?: number | null;
  utm_n?: number | null;
}

/** Sugestao do sistema para a relacao exemplar x lote (migration 065). */
export interface LotRelationSuggestionApi {
  code: string;
  reason: string;
  candidates: string[];
  tolerance_m: number;
  metrics: Record<string, unknown>;
  version: string;
  reference?: LotReferenceApi | null;
}

/**
 * Lote ao qual o exemplar se refere: o que o contém (CONTIDO) ou o que está em
 * frente a ele na calçada (EM_FRENTE). CEP nulo quando o logradouro tem vários
 * e nada os desempata — nesse caso `cep_candidates` lista as opções.
 */
export interface LotReferenceApi {
  kind: "CONTIDO" | "EM_FRENTE" | null;
  lot_number?: string | null;
  lot_registration?: string | null;
  block_number?: string | null;
  block_code?: string | null;
  adjacent_lot_number?: string | null;
  adjacent_lot_registration?: string | null;
  adjacent_block_number?: string | null;
  street?: string | null;
  street_distance_m?: number | null;
  cep?: string | null;
  cep_status?: "UNICO_NO_BAIRRO" | "UNICO_DO_LOGRADOURO" | "MAIS_PROXIMO" | "AMBIGUO" | "NAO_ENCONTRADO" | "SEM_LOGRADOURO";
  cep_candidates?: string[];
}

export interface TreeLotRelationApi {
  tree_id: number;
  cta_code: string;
  suggestion: LotRelationSuggestionApi | null;
  suggestion_stored: boolean;
  suggested_at: string | null;
  lot_relation: string | null;
  method: string | null;
  confirmed_by: number | null;
  confirmed_by_name: string | null;
  confirmed_at: string | null;
  platform: string | null;
  notes: string | null;
  lot_number: string | null;
  lot_registration: string | null;
  block_number?: string | null;
  block_code?: string | null;
  postal_code?: string | null;
  reference_kind?: "CONTIDO" | "EM_FRENTE" | null;
}

export interface LotRelationConfirmPayload {
  lot_relation: string;
  method: "CONFIRMADO_CAMPO" | "INFORMADO_ESCRITORIO";
  notes?: string;
}

/** A tree near a point, for the duplicate check before registering a new one. */
export interface NearbyTreeApi {
  id: number;
  uuid: string;
  cta_code: string;
  public_code: string | null;
  status: string | null;
  latitude: number | null;
  longitude: number | null;
  species_popular_name: string | null;
  species_scientific_name: string | null;
  distance_m: number;
}

/**
 * One candidate position for a typed address.
 *
 * Always a *proposed* position: this base has no house numbers, so the operator
 * still places the trunk. `extent_m` is the size of what was matched — the
 * length of the street or the area of the lot — which is how far off the point
 * can be.
 */
export interface GeocodeResultApi {
  kind: "LOGRADOURO" | "LOTE" | "CEP";
  label: string;
  latitude: number;
  longitude: number;
  precision: "STREET" | "PARCEL" | "CEP";
  extent_m: number | null;
  street: string | null;
  neighborhood: string | null;
  region?: string | null;
  cadastral_block_code: string | null;
  lot_number: string | null;
  lot_registration: string | null;
  cep: string | null;
}

/** Base photo of the registration (FIQ-1 §12). */
export interface TreeBasePhotoApi {
  tree_id: number;
  cta_code: string | null;
  has_photo: boolean;
  file_path: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  checksum_sha256: string | null;
  client_checksum_sha256: string | null;
  origin_match: boolean | null;
  uploaded_by: number | null;
  uploaded_platform: string | null;
  uploaded_at: string | null;
  replaced_previous: boolean;
}

export interface TreeCreateResponseApi {
  id: number;
  uuid: string;
  cta_code: string;
  status: string;
  duplicate_of: number | null;
  is_new: boolean;
}

export interface TreeUpdatePayload {
  species_id?: number | null;
  latitude?: number;
  longitude?: number;
  positioning_method?: "WEBGIS_CLICK" | "DEVICE_GPS" | "MANUAL_COORDINATES" | "GEOCODING";
  location_accuracy_m?: number;
  neighborhood?: string;
  street?: string;
  address_reference?: string;
  field_notes?: string;
  reason: string;
}

export interface TreeUpdateResponseApi {
  id: number;
  cta_code: string | null;
  changed_fields: string[];
  event_id: number;
  description: string;
}

export const campaignApi = {
  list: (filters?: { project_id?: number; status?: string }) =>
    apiFetch<CampaignSummaryApi[]>("/campaigns", { query: filters }),
  get: (id: number) =>
    apiFetch<CampaignSummaryApi>(`/campaigns/${id}`),
  create: (payload: CampaignCreatePayload) =>
    apiFetch<{ id: number; uuid: string; code: string; name: string; status: string; created_at: string }>(
      "/campaigns", { method: "POST", body: JSON.stringify(payload) },
    ),
  update: (id: number, payload: CampaignUpdatePayload) =>
    apiFetch<CampaignSummaryApi>(`/campaigns/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  delete: (id: number) =>
    apiFetch<void>(`/campaigns/${id}`, { method: "DELETE" }),
  members: (campaignId: number) =>
    apiFetch<CampaignMemberApi[]>(`/campaigns/${campaignId}/members`),
  addMember: (campaignId: number, payload: CampaignMemberCreatePayload) =>
    apiFetch<CampaignMemberApi>(`/campaigns/${campaignId}/members`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  removeMember: (campaignId: number, memberId: number) =>
    apiFetch<void>(`/campaigns/${campaignId}/members/${memberId}`, { method: "DELETE" }),
  quadras: (campaignId: number) =>
    apiFetch<CampaignQuadraProgressApi[]>(`/campaigns/${campaignId}/quadras`),
  addQuadra: (campaignId: number, payload: CampaignQuadraAddPayload) =>
    apiFetch<CampaignQuadraProgressApi[]>(`/campaigns/${campaignId}/quadras`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  createTree: (payload: TreeCreatePayload) =>
    apiFetch<TreeCreateResponseApi>("/trees", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  /**
   * Corrige o cadastro do exemplar (PATCH /trees/{id}). Exige `tree.update`;
   * o motivo e obrigatorio e a correcao entra na linha do tempo.
   * Faz o fetch direto para repassar a mensagem do servidor ao usuario.
   */
  updateTree: async (treeId: number, payload: TreeUpdatePayload): Promise<TreeUpdateResponseApi> => {
    const token = getToken();
    const res = await fetch(buildUrl(`/trees/${treeId}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      if (res.status === 403) throw new Error("Seu perfil não tem permissão para alterar o cadastro.");
      const detail = data && typeof data.detail === "string" ? data.detail : `Erro ${res.status}`;
      throw new Error(detail);
    }
    return data as TreeUpdateResponseApi;
  },
  /**
   * Trees within radius_m of a point, nearest first.
   *
   * Proximity raises a possible-duplicate warning for the operator to resolve;
   * it never merges records on its own.
   */
  nearbyTrees: (params: {
    latitude: number;
    longitude: number;
    radius_m?: number;
    project_id?: number;
  }) => apiFetch<NearbyTreeApi[]>("/trees/nearby", { query: params }),
  territorialContext: (params: { latitude: number; longitude: number; accuracy_m?: number }) =>
    apiFetch<PointTerritorialContextApi>("/trees/territorial-context", { query: params }),
  treeLotRelation: (treeId: number) => apiFetch<TreeLotRelationApi>(`/trees/${treeId}/lot-relation`),
  confirmLotRelation: (treeId: number, body: LotRelationConfirmPayload) =>
    apiFetch<TreeLotRelationApi>(`/trees/${treeId}/lot-relation`, { method: "POST", body: JSON.stringify(body) }),
  /**
   * Address search over local layers only — street names, cadastral lots and
   * CEPs. Nothing about the search leaves the system.
   */
  geocode: (q: string, limit = 8) =>
    apiFetch<GeocodeResultApi[]>("/trees/geocode", { query: { q, limit } }),
  treePhotoMetadata: (treeId: number) =>
    apiFetch<TreeBasePhotoApi>(`/trees/${treeId}/photo/metadata`),
  treePhotoUrl: (treeId: number) => apiFetchBlobUrl(`/trees/${treeId}/photo`),
  uploadTreePhoto: (treeId: number, file: File, clientChecksumSha256?: string) => {
    const form = new FormData();
    form.append("file", file);
    // Lets the server refuse a file that changed between the browser and here,
    // the same contract the mobile app uses for inspection evidence.
    if (clientChecksumSha256) form.append("client_checksum_sha256", clientChecksumSha256);
    return apiUpload<TreeBasePhotoApi>(`/trees/${treeId}/photo`, form);
  },
};

// --- Settings ---------------------------------------------------------------

export interface SettingResponse {
  key: string;
  value: Record<string, unknown>;
}

export const settingsApi = {
  get: (key: string) =>
    apiFetch<SettingResponse>(`/settings/${encodeURIComponent(key)}`),
  put: (key: string, value: Record<string, unknown>) =>
    apiFetch<SettingResponse>(`/settings/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),
};

// --- IAM (Identity & Access Management) ------------------------------------

export interface IamPlatformApi {
  id: number;
  uuid: string;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

export interface IamModuleApi {
  id: number;
  uuid: string;
  code: string;
  name: string;
  description: string | null;
  platform_id: number;
  parent_module_id: number | null;
  sort_order: number;
}

export interface IamPermissionApi {
  id: number;
  uuid: string;
  code: string;
  name: string;
  description: string | null;
  module_id: number;
  action_type: string;
  is_active: boolean;
}

export interface IamRoleApi {
  id: number;
  uuid: string;
  code: string;
  name: string;
  description: string | null;
  platform_id: number;
  is_system: boolean;
  is_active: boolean;
}

export interface IamMemberApi {
  user_id: number;
  user_uuid?: string;
  user_name: string;
  user_email: string;
  membership_id: number;
  membership_uuid?: string;
  status: string;
  matricula?: string;
  valid_from?: string;
  roles: string[];
  platforms: string[];
}

export interface IamCreateUserRequest {
  name: string;
  email: string;
  phone?: string;
  password: string;
  role_code: string;
}

export interface IamCreateUserResponse {
  user_id: number;
  user_uuid: string;
  email: string;
  name: string;
  membership_id: number;
  role_code: string;
  platforms: string[];
}

export interface IamUpdateUserRequest {
  name?: string;
  phone?: string;
}

export interface IamChangeRoleRequest {
  role_code: string;
}

export interface IamAuditEventApi {
  id: number;
  uuid: string;
  event_type: string;
  actor_user_id: number | null;
  target_user_id: number | null;
  organization_id: number | null;
  platform_id: number | null;
  role_id: number | null;
  permission_code: string | null;
  scope_type: string | null;
  scope_ref_id: number | null;
  ip_address: string | null;
  user_agent: string | null;
  details_json: Record<string, unknown> | null;
  reason: string | null;
  created_at: string;
}

export interface IamAuditEventsResponse {
  events: IamAuditEventApi[];
  limit: number;
  offset: number;
}

export interface IamAccessManifestApi {
  user_id: number;
  organization_id: number;
  platforms: Record<string, {
    roles: string[];
    permissions: string[];
    scopes: Array<{ scope_type: string; reference_id: number | null }>;
  }>;
}

export interface IamCheckRequest {
  permission: string;
  platform?: string;
  context?: Record<string, unknown>;
}

export interface IamCheckResponse {
  allowed: boolean;
  decision: string;
  step_denied: string | null;
  reason: string | null;
}

export const iamApi = {
  getAccessManifest: () =>
    apiFetch<IamAccessManifestApi>("/iam/me/access-manifest"),

  getMyPermissions: (platform?: string) =>
    apiFetch<{ permissions: string[] }>("/iam/me/permissions", {
      query: platform ? { platform } : undefined,
    }),

  checkPermission: (payload: IamCheckRequest) =>
    apiFetch<IamCheckResponse>("/iam/check", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  listPlatforms: () =>
    apiFetch<IamPlatformApi[]>("/iam/platforms"),

  listModules: (platform?: string) =>
    apiFetch<IamModuleApi[]>("/iam/modules", {
      query: platform ? { platform } : undefined,
    }),

  listPermissions: () =>
    apiFetch<IamPermissionApi[]>("/iam/permissions"),

  listRoles: () =>
    apiFetch<IamRoleApi[]>("/iam/roles"),

  listOrganizationMembers: (organizationId: number) =>
    apiFetch<IamMemberApi[]>(`/iam/organizations/${organizationId}/members`),

  listAuditEvents: (params?: {
    limit?: number;
    offset?: number;
    event_type?: string;
    target_user_id?: number;
    organization_id?: number;
  }) =>
    apiFetch<IamAuditEventsResponse>("/iam/audit-events", {
      query: params as Record<string, string | number | undefined>,
    }),

  createUser: (payload: IamCreateUserRequest) =>
    apiFetch<IamCreateUserResponse>("/iam/users", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateUser: (userId: number, payload: IamUpdateUserRequest) =>
    apiFetch<{ user_id: number; updated: boolean }>(`/iam/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  changeUserRole: (userId: number, payload: IamChangeRoleRequest) =>
    apiFetch<{ user_id: number; new_role_code: string; platforms: string[] }>(
      `/iam/users/${userId}/role`,
      { method: "PATCH", body: JSON.stringify(payload) },
    ),

  deactivateUser: (userId: number) =>
    apiFetch<{ user_id: number; status: string }>(
      `/iam/users/${userId}/deactivate`,
      { method: "POST" },
    ),

  reactivateUser: (userId: number) =>
    apiFetch<{ user_id: number; status: string }>(
      `/iam/users/${userId}/reactivate`,
      { method: "POST" },
    ),
};



export interface DashboardOverviewApi {
  total_trees: number;
  trees_without_validated_inspection: number;
  total_projects: number;
  active_projects: number;
  total_species_in_use: number;
  total_inspections: number;
  inspections_by_status: Array<{ label: string; count: number }>;
  pending_review: number;
  active_campaigns: number;
  health_distribution: Array<{ label: string; count: number }>;
  risk_distribution: Array<{ label: string; count: number }>;
  top_species: Array<{ label: string; count: number }>;
  registration_origin: Array<{ label: string; count: number }>;
  monthly_activity: Array<{ month: string; trees_registered: number; inspections_done: number }>;
  recent_inspections: Array<{
    id: number;
    tree_id: number;
    tree_code: string;
    species_name: string | null;
    inspection_type: string | null;
    workflow_status: string | null;
    inspection_date: string | null;
    inspector_name: string | null;
  }>;
  attention_trees: Array<{
    tree_id: number;
    tree_code: string;
    species_name: string | null;
    risk_class: string;
    primary_recommendation: string | null;
    last_inspection_date: string | null;
  }>;
  projects: Array<{ id: number; name: string; status: string | null; tree_count: number }>;
}


// Vegetacao por satelite (Sentinel-2, migration 066). NDVI e indice, nao % de copa.
export interface SatelliteRunApi {
  id: number;
  status: "RUNNING" | "SUCCEEDED" | "PARTIAL" | "FAILED";
  period_from: string;
  period_to: string;
  quadras_total: number | null;
  quadras_ok: number | null;
  observations_saved: number | null;
  started_at: string;
  finished_at: string | null;
}

export interface QuadraVegetationApi {
  quadra_id: number;
  code: string;
  name: string | null;
  area_m2: number | null;
  geometry: { type: string; coordinates: unknown };
  scene_date: string | null;
  ndvi_mean: number | null;
  ndvi_median: number | null;
  veg_fraction_ndvi_030: number | null;
  veg_fraction_ndvi_050: number | null;
  valid_fraction: number | null;
  observation_count: number;
  inspection_code: string | null;
  fita_canopy_percent: number | null;
}

export interface QuadrasVegetationApi {
  area_piloto_id: number;
  source: string;
  method_version: string;
  pixel_size_m: number;
  native_resolution_m: number;
  last_run: SatelliteRunApi | null;
  quadras: QuadraVegetationApi[];
}

export interface VegetationObservationApi {
  scene_date: string;
  ndvi_mean: number | null;
  ndvi_median: number | null;
  veg_fraction_ndvi_030: number | null;
  veg_fraction_ndvi_050: number | null;
  valid_fraction: number | null;
  quadras: number | null;
}

export interface VegetationSeriesApi {
  source: string;
  method_version: string;
  quadra_id: number | null;
  quadra_code: string | null;
  area_piloto_id: number | null;
  observations: VegetationObservationApi[];
}

export interface BairroApi {
  id: string;
  name: string;
  region: string | null;
  population: number | null;
  tree_count: number;
  assessed_count: number;
  high_risk_count: number;
  poor_health_count: number;
  analyzed_blocks: number;
  avg_canopy_percent: number | null;
  avg_impervious_percent: number | null;
  high_heat_blocks: number;
  high_deficit_blocks: number;
  estimated_tree_count: number | null;
  planting_potential_count: number | null;
}


export interface TreeEventApi {
  id: number;
  uuid: string;
  event_type: string;
  event_date: string;
  description: string | null;
  actor_user_id: number | null;
  actor_name: string | null;
  created_at: string;
}

export interface TreeEvidenceApi {
  evidence_id: number;
  inspection_id: number;
  inspection_type: string;
  inspection_date: string;
  workflow_status: string;
  evidence_type: string | null;
  original_filename: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  description: string | null;
  captured_at: string | null;
  uploaded_at: string;
  uploaded_by_name: string | null;
  invalidated_at: string | null;
  invalidation_reason: string | null;
  classified_at?: string | null;
  classified_by_name?: string | null;
}

export interface EvidenceClassificationEntryApi {
  id: number;
  previous_type: string | null;
  new_type: string | null;
  inspection_status: string;
  reason: string | null;
  changed_platform: string | null;
  changed_at: string;
  changed_by_name: string | null;
}

// ---------------------------------------------------------------------------
// Ocorrencias arboreas (OC-1).  Organizacao, autor e plataforma vem da sessao.
// ---------------------------------------------------------------------------

export interface OccurrenceCreatePayload {
  occurrence_type: string;
  description: string;
  channel: string;
  latitude: number;
  longitude: number;
  location_accuracy_m?: number | null;
  positioning_method?: string | null;
  occurred_at?: string | null;
  street?: string | null;
  neighborhood?: string | null;
  address_reference?: string | null;
  project_id?: number | null;
  /** Tres estados: null = nao informado, nunca "nao". */
  blocks_road?: boolean | null;
  hits_power_grid?: boolean | null;
  has_property_damage?: boolean | null;
  has_victims?: boolean | null;
  emergency_action_taken?: boolean | null;
  emergency_action_description?: string | null;
  trees?: { tree_id: number; link_role: "PRINCIPAL" | "ADJACENTE" }[];
  client_operation_id?: string;
}

export interface OccurrenceSummaryApi {
  id: number;
  uuid: string;
  protocol: string;
  occurrence_type: string;
  status: string;
  priority: string | null;
  suggested_priority: string;
  channel: string;
  reported_at: string;
  occurred_at: string | null;
  latitude: number;
  longitude: number;
  street: string | null;
  neighborhood: string | null;
  assigned_user_id: number | null;
  assigned_team_id: number | null;
  tree_count: number;
}

export interface OccurrenceDetailApi extends OccurrenceSummaryApi {
  description: string;
  address_reference: string | null;
  blocks_road: boolean | null;
  hits_power_grid: boolean | null;
  has_property_damage: boolean | null;
  has_victims: boolean | null;
  emergency_action_taken: boolean | null;
  emergency_action_description: string | null;
  suggested_priority_reasons: string[];
  cancel_reason: string | null;
  duplicate_of_id: number | null;
}

export interface NearbyOccurrenceApi {
  id: number;
  protocol: string;
  occurrence_type: string;
  status: string;
  reported_at: string;
  distance_m: number;
}

export interface OccurrenceCreateResponseApi {
  occurrence: OccurrenceDetailApi;
  replayed: boolean;
  possible_duplicates: NearbyOccurrenceApi[];
}

export interface OccurrenceHistoryEntryApi {
  id: number;
  previous_status: string | null;
  new_status: string;
  action: string;
  reason: string | null;
  changed_by_name: string | null;
  changed_at: string;
}

export const occurrenceApi = {
  create: (body: OccurrenceCreatePayload) =>
    apiFetch<OccurrenceCreateResponseApi>("/occurrences", { method: "POST", body: JSON.stringify(body) }),
  listForTree: (treeId: number) => apiFetch<OccurrenceSummaryApi[]>(`/trees/${treeId}/occurrences`),
  get: (id: number) => apiFetch<OccurrenceDetailApi>(`/occurrences/${id}`),
  history: (id: number) => apiFetch<OccurrenceHistoryEntryApi[]>(`/occurrences/${id}/history`),
};
